import { useRef, useContext, useState } from "react";
import FileInput from "components/ui/file-input";
import { useNavigate } from "react-router-dom";
import { AppContext } from "app-provider";
import { Formik, Field, ErrorMessage } from "formik";
import { Button, Dropdown } from "semantic-ui-react";
import { Loading, InfoPopup } from "components/ui";
import Modal from "components/ui/modal";
import MultiClientTrackingPreview from "./tracking-preview";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import api from "api";

// Multi-client tracking import page (S3DEV2-7448). A single vendor file (e.g. McKesson) can carry rows for
// many client companies; the vendor rule drives grouping/matching, and the optional company scope narrows
// which clients' orders are eligible (empty = match across all companies). Unlike the per-company Tracking
// tab, importer_type is fixed to "Tracking" and the request carries `vendor` + optional `company_ids`.
const Tracking = () => {
  const fileRef = useRef();
  const modalRef = useRef();
  const { companies, tracking_vendors } = useContext(AppContext);
  const [previewData, setPreviewData] = useState();
  const navigate = useNavigate();

  const companyOptions = companies.map((c) => ({
    key: c.value,
    text: c.text,
    value: c.value,
  }));

  const buildFormData = (values, { forStart }) => {
    const formData = new FormData();
    formData.append("file", values.file);
    formData.append("importer_type", "Tracking");
    formData.append("vendor", values.vendor);
    // Optional company scope: repeated key binds to the API's List<string> company_ids (empty = global).
    (values.company_ids ?? []).forEach((id) => formData.append("company_ids", id));
    formData.append("should_notify", values.should_notify);
    formData.append("schedule_import", values.schedule_import);
    if (forStart) {
      formData.append("use_system_user", values.use_system_user);
    }
    return formData;
  };

  const startImportMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await api.post(`/api/import/start`, formData);
      toast.success("Import Started");
      navigate(`/history/${response.data}`);
      return response.data;
    },
  });

  const previewImportMutation = useMutation({
    mutationFn: async (formData) => {
      const response = await api.post(`/api/import/preview`, formData);
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      modalRef.current.open();
    },
  });

  return (
    <div>
      <h1 style={{ padding: 10 }}>Tracking Import</h1>
      <p style={{ padding: "0 10px 10px", color: "#666", fontStyle: "italic" }}>
        Headers and settings for tracking companies can be edited under the{" "}
        <strong>Sleep Solutions and Services</strong> company profile.
      </p>
      <Formik
        initialValues={{
          file: null,
          vendor: "",
          company_ids: [],
          should_notify: true,
          use_system_user: true,
          schedule_import: "",
        }}
        onSubmit={(values) => {
          startImportMutation.mutate(buildFormData(values, { forStart: true }));
        }}
        validationSchema={Yup.object().shape({
          vendor: Yup.string().required("Required"),
          file: Yup.mixed().required("Required"),
        })}
      >
        {({ setFieldValue, handleSubmit, values, handleChange }) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              padding: 10,
              margin: 10,
              alignItems: "center",
              fontSize: "1.5em",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                border: "1px solid #ccc",
                background: "#efefe",
                width: "100%",
                padding: 30,
                display: "grid",
                gridTemplateColumns: "auto",
                gridGap: 30,
              }}
            >
              <div>
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <label style={{ paddingRight: 20 }}>Vendor</label>
                  <ErrorMessage
                    name="vendor"
                    render={(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  />
                </div>
                <select
                  name="vendor"
                  value={values.vendor}
                  onChange={handleChange}
                  style={{ marginTop: 15 }}
                >
                  <option />
                  {tracking_vendors.map((x) => (
                    <option key={x.value} value={x.value}>
                      {x.text}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ paddingRight: 20 }}>
                  Company Scope
                  <InfoPopup>
                    Optional. Limit matching to the selected client companies. Leave blank to match
                    orders across all companies.
                  </InfoPopup>
                </label>
                <div style={{ marginTop: 15, fontSize: "0.7em" }}>
                  <Dropdown
                    placeholder="All companies"
                    fluid
                    multiple
                    search
                    selection
                    options={companyOptions}
                    value={values.company_ids}
                    onChange={(e, { value }) => setFieldValue("company_ids", value)}
                  />
                </div>
              </div>

              <div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    marginBottom: 15,
                  }}
                >
                  <label style={{ paddingRight: 20 }}>File</label>
                  <ErrorMessage
                    name="file"
                    render={(msg) => <div style={{ color: "red" }}>{msg}</div>}
                  />
                </div>
                <FileInput
                  ref={fileRef}
                  onChange={(event) => {
                    setFieldValue("file", event.currentTarget.files[0]);
                  }}
                  onRemove={() => {
                    setFieldValue("file", null);
                  }}
                />
              </div>

              <label style={{ display: "flex", padding: 10 }}>
                <Field type="checkbox" name="should_notify" />
                <span style={{ paddingLeft: 10 }}>Email me when completed</span>
              </label>
              <label style={{ display: "flex", padding: 10 }}>
                <Field type="checkbox" name="use_system_user" />
                <span style={{ paddingLeft: 10 }}>
                  Change logs - System Importer User
                  <InfoPopup>
                    Order Change Logs will show the System Importer User as the user who made the
                    change instead of the currently logged in user
                  </InfoPopup>
                </span>
              </label>

              <div>
                <label style={{ paddingRight: 50 }}>
                  <span style={{ paddingRight: 10 }}>Schedule Import</span>
                  <InfoPopup>
                    Delay execution of import until specified date/time.
                  </InfoPopup>
                </label>
                <Field
                  type="datetime-local"
                  name="schedule_import"
                  min={new Date().toISOString().slice(0, -8)}
                />
                {values.schedule_import && (
                  <button onClick={() => setFieldValue("schedule_import", "")}>Clear</button>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  flex: 1,
                  justifyContent: "space-between",
                }}
              >
                <Button
                  type="button"
                  onClick={() => {
                    previewImportMutation.mutate(buildFormData(values, { forStart: false }));
                  }}
                >
                  Preview File
                </Button>
                <Button type="submit" onClick={handleSubmit}>
                  Start Importer
                </Button>
              </div>
            </div>
          </div>
        )}
      </Formik>
      {(previewImportMutation.isPending || startImportMutation.isPending) && <Loading />}

      <Modal ref={modalRef}>
        {previewData && <MultiClientTrackingPreview data={previewData} />}
      </Modal>
    </div>
  );
};

export default Tracking;
